import React, { FunctionComponent, useState } from 'react'
import { Avatar } from '@afterpaytouch/core'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import {
  useAuthentication,
  useConsumerName,
  useConsumerSelector,
  useInitialData,
  useIsPersistentAndOptedIn,
  useIsRecognizedViaPersistentLogin,
  useIsTrustedCheckout,
  useReadyFeatureFlag,
} from '../../state'
import { useRoutePageName } from '../../config/router'
import { Features } from '../../model'
import { isAfterpayInAppBrowser, isCashAppInAppBrowser } from '../../utils/device'
import { useTranslation } from '../../utils/convergence'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { AccountModal } from '../AccountModal'
import styles from './style.module.css'

/**
 * Whether the recognized Auto Remember Me profile chip should render. Shared by the chip itself and by
 * the Header (which suppresses the summary static avatar when the chip shows) so the two gates can never
 * drift. All conditions are store/flag reads that are safe on every global-header render:
 *
 * - `isPersistentAndOptedIn` — the session is persistent and opted in.
 * - `isRecognizedViaPersistentLogin` — the session was *silently recognized* at resume, not freshly
 *   minted this visit. A normal AP-cohort password/OTP login mints a persistent session on LoggedIn
 *   (status.persistent → true) while this provenance flag stays false, so without it the chip would
 *   render on a fresh login. Same gate the welcome-back transient uses, kept in lockstep.
 * - `!isTrustedCheckout` — exclude Trusted Checkout / OAuth resumes. They also set `persistent`, but
 *   `useStartCheckoutLoginMethod` classifies them ahead of ARM, so they are not Auto Remember Me.
 * - `isLongLivedSessionEnabled` — the live per-uuid kill switch (via useReadyFeatureFlag): hidden until
 *   LD has identified the current consumer, then their own value, bypassing the cross-consumer cache.
 * - `isArmSurface` — mirror useStartCheckoutLoginMethod's ARM-surface gates: iframes and managed
 *   Afterpay/Cash app webviews mint as legacy Web Persistent (not Auto Remember Me), so the chip must
 *   not show there. (Flag source intentionally differs from that hook — the chip uses the readiness-
 *   gated per-consumer read above rather than the raw useFlag the analytics label uses.)
 * - `Boolean(uuid)` — the account profile has loaded. On a verify=true persistent resume the session is
 *   persistent before accountLookup runs (orchestrator routes to VERIFY first); without this the chip
 *   could render with a blank/stale name/email — and expose logout — before the account loads.
 * - `!verificationRequired` — the resume isn't pending 2FA. uuid can be stale from a prior same-tab
 *   resume (consumer slice is sessionStorage-persisted), so this is the authoritative "past the verify
 *   step" signal alongside the account-load check.
 *
 * Cohort is intentionally not gated here: the flag's per-uuid targeting already scopes this to the AP
 * remember-me cohort, and useCohort is frequently undefined on the persistent-login resume route.
 */
export const useShouldShowProfileAvatarChip = (): boolean => {
  const isPersistentAndOptedIn = useIsPersistentAndOptedIn()
  const isRecognizedViaPersistentLogin = useIsRecognizedViaPersistentLogin()
  const isTrustedCheckout = useIsTrustedCheckout()
  const isLongLivedSessionEnabled = useReadyFeatureFlag<boolean>(Features.CHECKOUT_LONG_LIVE_SESSION_ENABLED, false)
  const { uuid } = useConsumerSelector()
  const { isIframe } = useInitialData()
  const { verificationRequired } = useAuthentication()
  const isArmSurface = !isIframe && !isAfterpayInAppBrowser() && !isCashAppInAppBrowser()
  return (
    isPersistentAndOptedIn &&
    isRecognizedViaPersistentLogin &&
    !isTrustedCheckout &&
    isLongLivedSessionEnabled &&
    isArmSurface &&
    Boolean(uuid) &&
    !verificationRequired
  )
}

/**
 * CASHDAP-3225: profile-avatar chip rendered in the checkout header for a recognized Auto Remember Me
 * session. Tapping it opens the account half-sheet (AccountModal) with a log-out affordance. Self-gates
 * via useShouldShowProfileAvatarChip, so it renders nothing for everyone else.
 */
export const ProfileAvatarChip: FunctionComponent = () => {
  const { t } = useTranslation()
  const consumerName = useConsumerName()
  const shouldShow = useShouldShowProfileAvatarChip()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const pageName = useRoutePageName()
  const [showModal, setShowModal] = useState(false)

  if (!shouldShow) {
    return null
  }

  const onAvatarClick = (): void => {
    logEvent(TrackingEvent.PRESSED_AUTO_REMEMBER_ME_PROFILE_AVATAR, { pageName })
    setShowModal(true)
  }

  return (
    <>
      {/*
        MaskedElement keeps the consumer's name out of Datadog: data-dd-privacy='mask' redacts the
        rendered avatar and the name-bearing attributes (aria-label/alt/title) from session replay,
        and data-dd-action-name overrides the click action's reported name so it isn't derived from
        the PII aria-label. The accessible label itself is preserved in the live DOM for screen readers.
      */}
      <MaskedElement actionName='Auto Remember Me Profile Avatar'>
        <button
          type='button'
          className={styles.chip}
          onClick={onAvatarClick}
          aria-label={t('common:rememberMeOpenAccountMenu', { name: consumerName })}
          data-testid='auto-remember-me-profile-avatar'
        >
          <Avatar size='S' src='' alt={consumerName} name={consumerName} title={consumerName} />
        </button>
      </MaskedElement>
      <AccountModal show={showModal} onClose={() => setShowModal(false)} />
    </>
  )
}
